import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { useSyncOuEnLigne } from '../context/SyncContext';
import { inboundRentalsAPI, transportStatementsAPI, urlImpressionEtat } from '../lib/inboundRentalsApi';
import { messageErreur } from '../lib/erreurs';
import { formatDate, formatNombre } from '../lib/formats';
import {
  calculerTotaux,
  corpsEnregistrement,
  creerGardeCle,
  ligneAffichee,
  lignesProposees,
  montantAvanceMax,
  nombreSaisi,
  nouvelleCle,
  saisieDepuis,
  totauxDepuisServeur,
} from '../lib/transportStatements';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { PrintMenu } from '../components/business/PrintMenu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { EmptyState } from '../components/ui/empty-state';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { StatutBadge } from '../components/ui/StatutBadge';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../components/ui/sonner';
import { AutresDepenses, Avances } from '../components/rentals/TransportStatementExtras';
import { LignesEtat, RecapEtat } from '../components/rentals/TransportStatementRecap';
import TransportStatementTrips from '../components/rentals/TransportStatementTrips';

const CADRE = 'mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8';

/**
 * Un état de paiement du transport: la facture entre la société et son
 * transporteur. En brouillon, on choisit des trajets dépotés, on ajuste les
 * prix, on coche les autres dépenses du camion et les avances; une fois validé
 * (numéros d'état et de facture attribués), l'état ne se modifie plus.
 *
 * Les montants affichés pendant la saisie sont un aperçu; dès l'enregistrement
 * ce sont ceux du serveur. Toute écriture porte une clé d'idempotence.
 */
const TransportStatementPage = () => {
  const { id, statementId } = useParams();
  const nouveau = !statementId;
  const navigate = useNavigate();
  const { canSeeMoney } = useAuth();
  // L'argent attend le serveur: boutons inactifs sans lui.
  const enLigne = useSyncOuEnLigne().isOnline !== false;

  const [contract, setContract] = useState(null);
  const [etat, setEtat] = useState(null);
  const [eligible, setEligible] = useState(null);
  const [saisie, setSaisie] = useState(null);
  const [failed, setFailed] = useState(false);
  const [modifie, setModifie] = useState(false);
  const [busy, setBusy] = useState('');
  const [dialogue, setDialogue] = useState(null); // 'valider' | 'annuler'
  const [motif, setMotif] = useState('');

  const modifieRef = useRef(false);
  const gardeEnregistrement = useRef(creerGardeCle('etat-enregistrer'));
  const gardeAnnulation = useRef(creerGardeCle('etat-annuler'));
  const cleValidation = useRef(nouvelleCle('etat-valider'));

  const retourContrat = `/rentals/inbound/${id}?onglet=etats`;

  const charger = useCallback(
    async ({ silencieux = false, forcer = false } = {}) => {
      if (!silencieux) setFailed(false);
      try {
        const [contrat, detail] = await Promise.all([
          inboundRentalsAPI.get(id),
          nouveau ? Promise.resolve(null) : transportStatementsAPI.get(id, statementId),
        ]);
        const brouillon = nouveau || detail?.status === 'BROUILLON';
        const choix = brouillon ? await transportStatementsAPI.eligible(id, nouveau ? undefined : statementId) : null;
        setContract(contrat);
        setEtat(detail);
        setEligible(choix);
        setFailed(false);
        if (forcer || !modifieRef.current) {
          setSaisie(brouillon ? saisieDepuis(detail, choix) : null);
          modifieRef.current = false;
          setModifie(false);
        }
      } catch (error) {
        if (!silencieux) {
          setFailed(true);
          toast.error(messageErreur(error, 'Impossible de charger cet état de paiement'));
        }
      }
    },
    [id, statementId, nouveau],
  );

  useEffect(() => {
    if (canSeeMoney) charger({ forcer: true });
  }, [charger, canSeeMoney]);

  useRafraichirSur(['locations', 'finance', 'trajets', 'depenses'], charger, { actif: canSeeMoney });

  const lecture = Boolean(etat && etat.status !== 'BROUILLON');
  const devise = etat?.currency || eligible?.defaults?.currency || contract?.currency || 'XOF';

  const lignesBrutes = useMemo(() => lignesProposees(eligible, etat), [eligible, etat]);

  const affichees = useMemo(() => {
    if (!saisie) return [];
    const enregistrees = new Map((etat?.lines || []).map((ligne) => [ligne.trip_id, ligne]));
    return lignesBrutes.map((ligne) =>
      ligneAffichee(ligne, saisie, devise, !modifie ? enregistrees.get(ligne.trip_id) || null : null),
    );
  }, [lignesBrutes, saisie, devise, etat, modifie]);

  const totauxLocaux = useMemo(() => {
    if (!saisie) return calculerTotaux([], {}, devise);
    const choisies = affichees.filter((ligne) => saisie.selectionnes.has(ligne.trip_id));
    const autresDepenses = (eligible?.charges || [])
      .filter((depense) => saisie.charges.has(depense.charge_key))
      .reduce((somme, depense) => somme + (Number(depense.amount) || 0), 0);
    const avances = (eligible?.advances || []).reduce(
      (somme, avance) => somme + (nombreSaisi(saisie.avances[avance.payment_id]) || 0),
      0,
    );
    return calculerTotaux(choisies, { autresDepenses, avances }, devise);
  }, [affichees, saisie, eligible, devise]);

  // Un brouillon propre montre les totaux du serveur; en cours de saisie, l'aperçu.
  const totaux = useMemo(() => {
    if (lecture || (!modifie && etat?.totals)) return totauxDepuisServeur(etat?.totals);
    return totauxLocaux;
  }, [lecture, modifie, etat, totauxLocaux]);

  const modifierSaisie = useCallback((changer) => {
    setSaisie((precedente) => (precedente ? changer(precedente) : precedente));
    modifieRef.current = true;
    setModifie(true);
  }, []);

  const changerChamp = (nom, valeur) => modifierSaisie((s) => ({ ...s, [nom]: valeur }));

  const basculer = (tripId) =>
    modifierSaisie((s) => {
      const suivants = new Set(s.selectionnes);
      if (suivants.has(tripId)) suivants.delete(tripId);
      else suivants.add(tripId);
      return { ...s, selectionnes: suivants };
    });

  const basculerTous = (tripIds, coche) =>
    modifierSaisie((s) => {
      const suivants = new Set(s.selectionnes);
      tripIds.forEach((tripId) => (coche ? suivants.add(tripId) : suivants.delete(tripId)));
      return { ...s, selectionnes: suivants };
    });

  const changerPrix = (tripId, valeur) =>
    modifierSaisie((s) => ({ ...s, prix: { ...s.prix, [tripId]: valeur } }));

  const appliquerPrix = (valeur) =>
    modifierSaisie((s) => ({
      ...s,
      prix: Object.fromEntries(lignesBrutes.map((ligne) => [ligne.trip_id, valeur])),
    }));

  const changerLivre = (tripId, valeur) =>
    modifierSaisie((s) => ({ ...s, livre: { ...s.livre, [tripId]: valeur } }));

  const basculerDepense = (cle) =>
    modifierSaisie((s) => {
      const suivantes = new Set(s.charges);
      if (suivantes.has(cle)) suivantes.delete(cle);
      else suivantes.add(cle);
      return { ...s, charges: suivantes };
    });

  const basculerAvance = (avance) => {
    if (saisie?.avances[avance.payment_id] !== undefined) {
      modifierSaisie((s) => {
        const { [avance.payment_id]: _retiree, ...reste } = s.avances;
        return { ...s, avances: reste };
      });
      return;
    }
    const montant = montantAvanceMax(avance, totauxLocaux.net);
    if (montant <= 0) {
      toast.info('Il ne reste rien à payer sur cet état');
      return;
    }
    modifierSaisie((s) => ({ ...s, avances: { ...s.avances, [avance.payment_id]: String(montant) } }));
  };

  const changerMontantAvance = (avance, valeur) => {
    const nombre = nombreSaisi(valeur);
    const borne = nombre !== null && nombre > Number(avance.available) ? String(avance.available) : valeur;
    modifierSaisie((s) => ({ ...s, avances: { ...s.avances, [avance.payment_id]: borne } }));
  };

  /** Enregistre le brouillon (création ou modification); rend l'état enregistré, ou `null`. */
  const sauvegarder = async () => {
    const corps = corpsEnregistrement({
      lignes: lignesBrutes,
      saisie,
      avances: eligible?.advances || [],
    });
    if (corps.trip_ids.length === 0) {
      toast.error('Choisissez au moins un trajet dépoté');
      return null;
    }
    const cle = gardeEnregistrement.current({ statementId: statementId || null, corps });
    try {
      const resultat = nouveau
        ? await transportStatementsAPI.create(id, { ...corps, idempotency_key: cle })
        : await transportStatementsAPI.update(id, statementId, { ...corps, idempotency_key: cle });
      toast.success('État enregistré');
      modifieRef.current = false;
      setModifie(false);
      if (nouveau && resultat?.id) {
        navigate(`/rentals/inbound/${id}/etats/${resultat.id}`, { replace: true });
      } else {
        await charger({ silencieux: true, forcer: true });
      }
      return resultat || null;
    } catch (error) {
      toast.error(messageErreur(error, 'Enregistrement de l’état impossible'));
      return null;
    }
  };

  const enregistrer = async () => {
    setBusy('enregistrer');
    try {
      await sauvegarder();
    } finally {
      setBusy('');
    }
  };

  const valider = async () => {
    setBusy('valider');
    try {
      if (modifieRef.current && !(await sauvegarder())) return;
      const resultat = await transportStatementsAPI.validate(id, statementId, cleValidation.current);
      toast.success(resultat?.reference ? `État ${resultat.reference} validé` : 'État validé');
      await charger({ silencieux: true, forcer: true });
    } catch (error) {
      toast.error(messageErreur(error, 'Validation de l’état impossible'));
    } finally {
      setDialogue(null);
      setBusy('');
    }
  };

  const annuler = async () => {
    setBusy('annuler');
    try {
      const raison = motif.trim();
      const cle = gardeAnnulation.current({ statementId, raison });
      await transportStatementsAPI.cancel(id, statementId, { idempotencyKey: cle, reason: raison || undefined });
      setDialogue(null);
      if (etat?.status === 'BROUILLON') {
        toast.success('Brouillon annulé');
        navigate(retourContrat, { replace: true });
      } else {
        toast.success('État annulé');
        await charger({ silencieux: true, forcer: true });
      }
    } catch (error) {
      setDialogue(null);
      toast.error(messageErreur(error, 'Annulation de l’état impossible'));
    } finally {
      setBusy('');
    }
  };

  const ouvrirAnnulation = () => {
    setMotif('');
    setDialogue('annuler');
  };

  if (!canSeeMoney) {
    return (
      <div className={CADRE}>
        <PageHeader eyebrow="Location reçue" title="État de paiement" backTo={`/rentals/inbound/${id}`} backLabel="Retour au contrat" />
        <EmptyState
          variant="error"
          title="Ces montants sont réservés à la direction"
          description="Demandez un profil avec montants visibles."
        />
      </div>
    );
  }

  // Les données affichées doivent être celles de l'adresse: après l'enregistrement
  // d'un nouvel état, l'adresse change avant que l'état enregistré soit relu.
  const donneesAJour = Boolean(contract) && (nouveau ? etat === null : etat?.id === statementId);

  if (!donneesAJour && !failed) {
    return (
      <div className={CADRE}>
        <LoadingState shape="detail" />
      </div>
    );
  }

  if (failed || !donneesAJour) {
    return (
      <div className={CADRE}>
        <PageHeader eyebrow="Location reçue" title="État de paiement" backTo={retourContrat} backLabel="Retour au contrat" />
        <EmptyState
          variant="error"
          title="Cet état de paiement n’a pas pu être chargé"
          description="Il n’existe pas ou n’appartient pas à votre société."
          actionLabel="Réessayer"
          onAction={() => charger({ forcer: true })}
        />
      </div>
    );
  }

  const statut = etat?.status || 'BROUILLON';
  const aEnregistrer = nouveau || modifie;
  const occupe = Boolean(busy);
  const ecritureBloquee = occupe || !enLigne;
  const nombreChoisis = lecture ? totaux.lignes : (saisie?.selectionnes.size ?? 0);
  const avertissements = !modifie && !lecture ? etat?.warnings || [] : [];
  const reference = etat?.reference || null;
  const formatRetenue = (valeur) => `${formatNombre(valeur, 0, '—')} FCFA/L`;

  return (
    <div className={CADRE} data-testid="transport-statement-page">
      <PageHeader
        eyebrow={`Location reçue · ${contract.lessor_name || 'Transporteur'}`}
        title={reference || 'État de paiement'}
        backTo={retourContrat}
        backLabel="Retour au contrat"
        stats={[
          { label: 'Trajets', valeur: nombreChoisis },
          { label: 'Net à payer', valeur: totaux.net, unite: 'FCFA', ton: 'positif', testid: 'etat-net' },
        ]}
        meta={
          <>
            <StatutBadge domaine="statut_etat_paiement" code={statut} data-testid="etat-statut" />
            {lecture && etat.bill?.payment_status ? (
              <StatutBadge domaine="statut_paiement_etat" code={etat.bill.payment_status} />
            ) : null}
            {!lecture && aEnregistrer ? <Badge variant="info">Non enregistré</Badge> : null}
            {lecture ? <span>Établie le {formatDate(etat.issued_at)}</span> : null}
            {lecture && etat.retention_price !== null && etat.retention_price !== undefined ? (
              <span>Retenue {formatRetenue(etat.retention_price)}</span>
            ) : null}
            {statut === 'ANNULE' && etat.cancel_reason ? <span>Motif : {etat.cancel_reason}</span> : null}
            {avertissements.length > 0 ? (
              <Aide libelle={`${avertissements.length} à revoir`} titre="À revoir" ton="attention" points={avertissements} />
            ) : null}
          </>
        }
        actions={
          <>
            {!lecture ? (
              <Button
                variant={aEnregistrer ? 'default' : 'outline'}
                onClick={enregistrer}
                disabled={ecritureBloquee || !aEnregistrer}
                data-testid="etat-enregistrer"
              >
                {busy === 'enregistrer' ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            ) : null}
            {!lecture && !nouveau ? (
              <Button
                variant={aEnregistrer ? 'outline' : 'default'}
                onClick={() => setDialogue('valider')}
                disabled={ecritureBloquee}
                data-testid="etat-valider"
              >
                Valider
              </Button>
            ) : null}
            {!nouveau && statut !== 'ANNULE' ? (
              <Button variant="outline" onClick={ouvrirAnnulation} disabled={ecritureBloquee} data-testid="etat-annuler">
                Annuler l’état
              </Button>
            ) : null}
            {!nouveau ? (
              <PrintMenu
                label="Imprimer"
                items={[
                  {
                    label: reference || 'Brouillon',
                    url: urlImpressionEtat(id, statementId),
                    filename: `etat-paiement-${reference || 'brouillon'}`,
                    formats: ['pdf', 'xlsx'],
                  },
                ]}
              />
            ) : null}
          </>
        }
      />

      {!lecture && saisie ? (
        <>
          <div className="flex flex-wrap items-end gap-4" data-testid="etat-entete-saisie">
            <div className="space-y-1">
              <Label htmlFor="etat-emis-le">Établie le</Label>
              <Input
                id="etat-emis-le"
                type="date"
                value={saisie.emisLe}
                disabled={ecritureBloquee}
                onChange={(event) => changerChamp('emisLe', event.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="etat-prix-retenue">Prix de retenue</Label>
              <div className="relative">
                <Input
                  id="etat-prix-retenue"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  value={saisie.prixRetenue}
                  disabled={ecritureBloquee}
                  onChange={(event) => changerChamp('prixRetenue', event.target.value)}
                  className="w-44 pr-16 text-right tabular-nums"
                  data-testid="etat-prix-retenue"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                  FCFA/L
                </span>
              </div>
            </div>
          </div>

          <TransportStatementTrips
            lignes={affichees}
            saisie={saisie}
            totaux={totaux}
            disabled={ecritureBloquee}
            onBasculer={basculer}
            onBasculerTous={basculerTous}
            onPrix={changerPrix}
            onLivre={changerLivre}
            onAppliquerPrix={appliquerPrix}
          />
          <AutresDepenses
            depenses={eligible?.charges || []}
            choisies={saisie.charges}
            disabled={ecritureBloquee}
            onBasculer={basculerDepense}
          />
          <Avances
            avances={eligible?.advances || []}
            saisie={saisie}
            disabled={ecritureBloquee}
            onBasculer={basculerAvance}
            onMontant={changerMontantAvance}
          />
        </>
      ) : null}

      {lecture ? (
        <>
          <LignesEtat lignes={etat.lines || []} totaux={totaux} />
          <AutresDepenses depenses={etat.charges || []} choisies={new Set()} lecture />
          <Avances avances={etat.advances || []} saisie={{ avances: {} }} lecture />
        </>
      ) : null}

      <RecapEtat totaux={totaux} bill={lecture ? etat.bill : null} apercu={modifie} />

      <AlertDialog open={dialogue === 'valider'} onOpenChange={(ouvert) => !ouvert && !busy && setDialogue(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Valider cet état ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les numéros d’état et de facture sont attribués et la facture du transporteur est enregistrée. L’état ne
              se modifie plus.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === 'valider'}>Pas maintenant</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                valider();
              }}
              disabled={busy === 'valider' || !enLigne}
              data-testid="etat-confirmer-validation"
            >
              {busy === 'valider' ? 'Validation…' : 'Valider l’état'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={dialogue === 'annuler'} onOpenChange={(ouvert) => !ouvert && !busy && setDialogue(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{statut === 'VALIDE' ? 'Annuler cet état ?' : 'Annuler ce brouillon ?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {statut === 'VALIDE'
                ? 'La facture du transporteur est extournée et les trajets redeviennent disponibles.'
                : 'Les trajets redeviennent disponibles pour un autre état.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {statut === 'VALIDE' ? (
            <div className="space-y-1.5">
              <Label htmlFor="etat-motif-annulation">Motif</Label>
              <Textarea
                id="etat-motif-annulation"
                rows={2}
                value={motif}
                onChange={(event) => setMotif(event.target.value)}
                data-testid="etat-motif-annulation"
              />
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === 'annuler'}>Garder l’état</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                annuler();
              }}
              disabled={busy === 'annuler' || !enLigne || (statut === 'VALIDE' && !motif.trim())}
              data-testid="etat-confirmer-annulation"
            >
              {busy === 'annuler' ? 'Annulation…' : 'Annuler l’état'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TransportStatementPage;
